/**
 * The station map an answer opens (`assistant_message.map`), as on the old kiosk: the level asked for,
 * every point with its icon, the categories the answer names lit and named, "you are here" always.
 * A tap on a point names it and lights its category; the legend below lights a category (again: off).
 * One finger drags it, two pinch, the wheel zooms, the buttons zoom and fit it again (pan-zoom.ts).
 * Data from `GET /api/maps/{station}`.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { Maximize, Minus, Plus } from 'lucide-react';
import { z } from 'zod';
import { describeError, report } from '@platform/core/diagnostics';
import { Panel, Problem } from '@platform/ui-kit/Panel';
import { defineWidget } from '@platform/widgets/contract';
import { usePanZoom } from './pan-zoom';
import { PoiMarker, type Poi } from './PoiMarker';
import { NOT_IN_LEGEND, styleOf, YOU_ARE_HERE } from './pois';
import css from './StationMap.module.css';

const poiSchema = z.object({ id: z.string(), category: z.string(), label: z.string(), x: z.number(), y: z.number() }).passthrough();
const levelSchema = z.object({
  id: z.string(),
  name: z.record(z.string(), z.string()).default({}),
  map_url: z.string(),
  view_box: z.object({ width: z.number(), height: z.number() }),
  pois: z.array(poiSchema).default([]),
}).passthrough();
const mapSchema = z.object({
  id: z.string(),
  name: z.string(),
  default_level: z.string().optional(),
  levels: z.array(levelSchema).min(1),
}).passthrough();
type StationMapData = z.infer<typeof mapSchema>;

const schema = z.object({
  station: z.string(),
  level: z.string().optional(),
  highlight: z.array(z.string()).default([]),
});

type Loaded = { state: 'loading' } | { state: 'ready'; map: StationMapData } | { state: 'failed'; why: string };

export const stationMap = defineWidget({
  key: 'station-map',
  schema,
  Component: function StationMap({ props, ctx }) {
    const { t, language } = ctx;
    const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });
    const [levelId, setLevelId] = useState<string | null>(null);
    // what the user chose on the map, over what the answer lit: a category, and the point tapped
    const [chosen, setChosen] = useState<{ category: string | null; poi: string | null } | null>(null);
    const pan = usePanZoom();

    useEffect(() => {
      let cancelled = false;
      fetch(`/api/maps/${encodeURIComponent(props.station)}`)
        .then(async (r) => {
          if (!r.ok) throw new Error(`/api/maps/${props.station} → ${r.status}`);
          return mapSchema.parse(await r.json());
        })
        .then((map) => !cancelled && setLoaded({ state: 'ready', map }))
        .catch((e) => {
          report('warn', 'widget:station-map', describeError(e));
          if (!cancelled) setLoaded({ state: 'failed', why: describeError(e) });
        });
      return () => { cancelled = true; };
    }, [props.station]);

    if (loaded.state === 'loading') return <Panel title={t('trains.map.loading')} icon="map"><span /></Panel>;
    if (loaded.state === 'failed') return <Problem title={t('trains.map.failed')} detail={loaded.why} detailLabel={t('error.details')} />;

    const { map } = loaded;
    const wanted = levelId ?? props.level ?? map.default_level;
    const level = map.levels.find((l) => l.id === wanted) ?? map.levels[0]!;
    const lit = new Set(chosen ? (chosen.category ? [chosen.category] : []) : props.highlight);
    const isLit = (p: Poi) => lit.has(p.category) || chosen?.poi === p.id;
    const marked = (l: (typeof map.levels)[number]) => l.pois.some((p) => props.highlight.includes(p.category));
    const legend = [...new Set(level.pois.map((p) => p.category))].filter((c) => !NOT_IN_LEGEND.has(c));
    // drawn in this order, so lit points and "you are here" stay on top
    const pois = [...level.pois].sort((a, b) => rank(a) - rank(b));
    function rank(p: Poi) {
      return p.category === YOU_ARE_HERE ? 2 : isLit(p) ? 1 : 0;
    }

    const tapPoi = (p: Poi) => setChosen({ category: p.category === YOU_ARE_HERE ? null : p.category, poi: p.id });
    const tapCategory = (c: string) => setChosen((now) => (now?.category === c && !now.poi ? { category: null, poi: null } : { category: c, poi: null }));

    return (
      <Panel title={t('trains.map.title', { station: map.name })} icon="map">
        <div className={css.layout}>
          {map.levels.length > 1 && (
            <div className={css.levels}>
              {map.levels.map((l) => (
                <button key={l.id} className={`${css.level} ${l.id === level.id ? css.levelActive : ''} ${marked(l) ? css.levelMarked : ''}`}
                  onClick={() => { setLevelId(l.id); pan.fit(); }}>
                  {l.name[language] ?? l.name.it ?? l.id}
                </button>
              ))}
            </div>
          )}
          <div className={css.map} ref={pan.frame} {...pan.handlers}>
            <div className={css.zoom} style={{ transform: `translate(${pan.view.x}px, ${pan.view.y}px) scale(${pan.view.scale})` }}>
              <svg className={css.svg} viewBox={`0 0 ${level.view_box.width} ${level.view_box.height}`} role="img" aria-label={map.name}>
                <image href={level.map_url} width={level.view_box.width} height={level.view_box.height} />
                {pois.map((p) => (
                  <PoiMarker key={p.id} poi={p} lit={isLit(p)} dimmed={lit.size > 0 && !isLit(p) && p.category !== YOU_ARE_HERE} onTap={tapPoi} />
                ))}
              </svg>
            </div>
            <div className={css.zoomButtons} onPointerDown={(e) => e.stopPropagation()}>
              <button onClick={pan.zoomIn} aria-label={t('trains.map.zoom_in')}><Plus size={22} /></button>
              <button onClick={pan.zoomOut} aria-label={t('trains.map.zoom_out')}><Minus size={22} /></button>
              <button onClick={pan.fit} disabled={pan.view.scale === 1} aria-label={t('trains.map.fit')}><Maximize size={20} /></button>
            </div>
          </div>
          {legend.length > 0 && (
            <div className={css.legend} role="group" aria-label={t('trains.map.legend')}>
              {legend.map((c) => {
                const style = styleOf(c);
                const Icon = style.icon;
                const on = lit.has(c);
                return (
                  <button key={c} className={`${css.chip} ${on ? css.chipOn : ''}`} aria-pressed={on} onClick={() => tapCategory(c)}
                    style={{ '--poi-bg': style.bg, '--poi-border': style.border, '--poi-ink': style.ink } as CSSProperties}>
                    <Icon size={14} />
                    {t(`trains.poi.${c}`)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Panel>
    );
  },
});
